import { useCallback, useEffect, useMemo, useRef } from "react";

import { useChatStore } from "@stores/chat.store";
import { useChatConversations } from "@hooks/api/chat-websocket";

import {
  API_REQUEST_STATUS,
  CHAT_CONVERSATION_TYPES,
  DEFAULT_TAGS,
} from "@/utils/constants";
import { getConversationType } from "@/utils/helpers";

import type {
  TConversationListSectionConfig,
  TConversationListSectionLogicResult,
} from "./conversation-list-section.types";
import {
  FILTER_DEBOUNCE_MS,
  TAB_ITEMS,
} from "./utils/conversation-list-section.constants";

/** Gerencia tabs, filtro por tags e fetch com debounce da seção de lista de conversas. */
const useConversationListSectionLogic = ({
  show,
}: TConversationListSectionConfig): TConversationListSectionLogicResult => {
  const {
    fetchConversations,
    resetConversations,
    setSelectedTags,
    resetPendingRequestCount,
    setConversationTabs,
  } = useChatConversations();
  const storeSelectedTabIndex = useChatStore((s) => s.tabs.conversation);
  const storeConversationStatus = useChatStore(
    (s) => s.conversation.status?.to,
  );
  const selectedConversation = getConversationType(storeSelectedTabIndex);

  const targetFetched = useChatStore((s) =>
    selectedConversation === "from" ? s.conversationFrom.fetched : s.conversationTo.fetched,
  );
  const targetLoading = useChatStore((s) =>
    selectedConversation === "from" ? s.conversationFrom.loading : s.conversationTo.loading,
  );

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRequestPending = useRef(false);
  const lastTabIndexRef = useRef(storeSelectedTabIndex);

  useEffect(() => {
    if (lastTabIndexRef.current === storeSelectedTabIndex) return;
    lastTabIndexRef.current = storeSelectedTabIndex;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    isRequestPending.current = false;
    resetPendingRequestCount("to");
    resetPendingRequestCount("from");
    setSelectedTags([]);

    if (!targetFetched && !targetLoading) {
      const tags =
        selectedConversation === CHAT_CONVERSATION_TYPES.sale
          ? DEFAULT_TAGS
          : null;
      fetchConversations(selectedConversation, undefined, tags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSelectedTabIndex]);

  const isShowConversationsFilter = Boolean(storeSelectedTabIndex);

  const handleFilterChange = useCallback(
    (newFilters: string[]) => {
      if (selectedConversation !== CHAT_CONVERSATION_TYPES.sale) return;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      setSelectedTags(newFilters);

      if (!isRequestPending.current) {
        resetConversations(selectedConversation);
      }
      isRequestPending.current = true;

      debounceTimeoutRef.current = setTimeout(() => {
        const tagsToFetch = newFilters.length > 0 ? newFilters : DEFAULT_TAGS;
        fetchConversations(selectedConversation, undefined, tagsToFetch);
      }, FILTER_DEBOUNCE_MS);
    },
    [
      selectedConversation,
      fetchConversations,
      setSelectedTags,
      resetConversations,
    ],
  );

  useEffect(() => {
    if (storeConversationStatus === API_REQUEST_STATUS.ready) {
      isRequestPending.current = false;
    }
  }, [storeConversationStatus]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const tabItemsWithAction = useMemo(
    () =>
      TAB_ITEMS.map((item) => ({
        ...item,
        clickAction: setConversationTabs,
      })),
    [setConversationTabs],
  );

  return {
    show,
    tabItems: tabItemsWithAction,
    isShowConversationsFilter,
    handleFilterChange,
  };
};

export { useConversationListSectionLogic };
